import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from 'rxjs';
import { MonthPipe } from './month.pipe';
import { DayPipe } from './day.pipe';
import { DayComponent } from './day/day.component';
import { DayToDatePipe } from './day-to-date.pipe';
import { DateHelper } from '@app/date-helper';
import { AvailableDate } from '@app/services/api/api.service';
import { DirectivesModule } from '@app/directives/directives.module';

@Component({
  selector: 'app-when',
  templateUrl: './when.component.html',
  styleUrls: ['./when.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    NgOptimizedImage,
    DayComponent,
    MonthPipe,
    DayPipe,
    DayToDatePipe,
    DirectivesModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: WhenComponent,
    },
  ],
})
export class WhenComponent implements ControlValueAccessor {
  @Input() public value: any;
  @Input() public active = false;

  @Input()
  public unavailableDates: AvailableDate[];
  @Input()
  public noAvailability = false;
  public focused = new BehaviorSubject(false);

  // todo control valuea accessor on each element
  @ViewChild('whenInput')
  public whenInput?: ElementRef;

  selection: any;
  days = Array.from({ length: 40 }, (_, i) => i + 1);
  selecting: boolean = false;

  anchorStart?: number;
  anchorEnd?: number;
  focus?: number;

  fromMonth!: number;
  fromYear!: number;

  toMonth!: number;
  toYear!: number;

  toDays!: number;
  fromDays!: number;

  toStartDay!: number;
  fromStartDay!: number;

  private touched = false;

  public edges = [];
  public corners = [];

  public startDayOfMonth: number | undefined;
  public endDayOfMonth: number | undefined;

  public absoluteAnchorStart: number | undefined = 0;
  public absoluteAnchorEnd: number | undefined = 0;
  public monthOffset = 0;

  private destroyed = new Subject<void>();

  public startDate: Date | undefined;
  public endDate: Date | undefined;

  public anchorStartDate: any;
  public anchorEndDate: any;

  onChange = (value: string[]) => {};
  onTouched = () => {};

  ngOnInit() {
    // const daysInMonth = getDaysInMonth(new Date(2023, 11, 1).getMonth());
    this.fromMonth = new Date().getMonth();
    this.fromYear = new Date().getFullYear();
    this.toYear = this.fromMonth === 11 ? this.fromYear + 1 : this.fromYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.fromMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);

    this.focused
      .pipe(
        tap((focused) => {
          if (focused && this.value) {
            // do something if date already was selected
          } else if (focused && !this.value) {
            // do something if date already was selected
          }
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['active']?.previousValue === false &&
      changes['active']?.currentValue
    ) {
      this.setFocus(true);
    }
  }

  // is this working correctly?
  public highlight(i: number) {
    if (this.anchorStart !== undefined) {
      if (i > this.anchorStart) {
        this.focus = i;
      }
    }
  }

  public unhighlight() {
    this.focus = undefined;
  }

  setFocus(focused: boolean) {
    this.focused.next(focused);
  }

  public clicked(day: number, dayOfMonth: number, checkDisabled = false) {
    const today = new Date().getDate();
    if (
      checkDisabled &&
      this.monthOffset === 0 &&
      today + this.fromStartDay > day
    ) {
      return;
    }
    this.selecting = true;
    if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart < this.monthOffset
    ) {
      // click on a future month
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
    } else if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart > this.monthOffset
    ) {
      // click on a previous month
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.endDate = undefined;
      this.anchorStartDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    } else if (day === this.anchorStart) {
      this.anchorStart = undefined;
      this.anchorStartDate = undefined;
      this.startDate = undefined;
      this.absoluteAnchorStart = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = undefined;
      this.endDayOfMonth = undefined;
    } else if (this.anchorStart !== undefined && this.anchorEnd !== undefined) {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    } else if (this.anchorStart !== undefined && day > this.anchorStart) {
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
    } else {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    }
  }

  private getDate(selected: number): Date {
    let year;
    let month;
    let day;

    if (selected > 40) {
      year = this.toYear;
      month = this.toMonth;
      day = selected - 40 - this.toStartDay;
    } else {
      year = this.fromYear;
      month = this.fromMonth;
      day = selected - this.fromStartDay;
    }

    return new Date(year, month, day);
  }

  public done(day: number) {
    // this.doUpdate();
  }

  public onBlurHandler() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  public doUpdate() {
    this.value = [DateHelper.formatDateToLocalString(this.startDate), DateHelper.formatDateToLocalString(this.endDate)];
    this.onChange(this.value);
    // this causes things to not work with ngif focused on surrounding el
    // this.setFocus(false);
  }

  public close() {
    this.focused.next(false);
  }

  reset() {
    this.anchorStart = undefined;
    this.anchorEnd = undefined;
    this.absoluteAnchorEnd = undefined;
    this.absoluteAnchorStart = undefined;
    this.value = undefined;
    this.startDayOfMonth = undefined;
    this.endDayOfMonth = undefined;
    this.onChange(this.value);
  }

  public nextMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart - 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd - 40;
    }
    this.monthOffset = this.monthOffset + 1;
    this.fromMonth = this.toMonth;
    this.fromYear = this.toYear;
    this.toYear = this.toMonth === 11 ? this.toYear + 1 : this.toYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.toMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  // shift anchors by 40! should I do this? seems like it messes things up
  public previousMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart + 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd + 40;
    }
    this.monthOffset = this.monthOffset - 1;
    this.toMonth = this.fromMonth;
    this.toYear = this.fromYear;
    this.fromYear = this.fromMonth === 0 ? this.fromYear - 1 : this.fromYear;
    if (this.fromMonth === 0) {
      this.fromMonth = 11;
    } else {
      this.fromMonth = this.fromMonth - 1;
    }

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
